/**
 * Transaction Status Utilities — Single Source of Truth
 *
 * Wins = PIF + CPP only. paying_installment is cash-but-not-a-win — see isInstallment().
 * All components should use these instead of inline status checks.
 */

/** Check if a transaction status represents a win (closed deal) */
export function isWin(status: string): boolean {
  return ["paid_in_full", "closed_payment_plan"].includes(status);
}

/** Check if a transaction is a recurring installment payment (cash-collected but NOT a win) */
export function isInstallment(status: string): boolean {
  return status === "paying_installment";
}

/** Check if a transaction status represents a loss */
export function isLoss(status: string): boolean {
  if (!status) return false;
  return status.toLowerCase().includes("lost");
}

/** Check if a transaction status represents a no-show or cancellation */
export function isNoShow(status: string): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes("no_show") || s === "rescheduled" || s.startsWith("cancel");
}

/** Check if a transaction is a new deal (win from a sales call) */
export function isNewDeal(status: string, eventType: string): boolean {
  return isWin(status) && eventType === "sales_call_outcome";
}

/** Get the category of a transaction status */
export function getStatusCategory(
  status: string
): "win" | "loss" | "no_show" | "installment" | "pending" {
  if (isWin(status)) return "win";
  if (isInstallment(status)) return "installment";
  if (isLoss(status)) return "loss";
  if (isNoShow(status)) return "no_show";
  return "pending";
}

/** All win statuses (for SQL IN clauses or filter arrays) */
export const WIN_STATUSES = ["paid_in_full", "closed_payment_plan"] as const;

/** Installment status constant (cash-collected but not a win) */
export const INSTALLMENT_STATUS = "paying_installment" as const;

/**
 * Statuses that count as a Show (lead was present).
 * PIF + CPP + Lost + Rescheduled + PendingClose. NEVER includes paying_installment or no_show.
 * SQL twin: sql/canonical/metric_predicates.sql is_show().
 *
 * pending_close added 2026-08-09 (Mako, R1): "Pending close is a show, yes, because
 * the only way they could be a pending close is if they showed up at the call."
 * This list read 4 statuses until then, which CONTRADICTED the SQL twin it names —
 * that twin has carried 5 since June. Verified against live pg_proc, not assumed.
 */
export const SHOW_STATUSES = [
  "paid_in_full",
  "closed_payment_plan",
  "lost",
  "rescheduled",
  "pending_close",
] as const;

/**
 * Statuses that count as a Decisive Call — the call reached a yes or a no.
 * Shows MINUS rescheduled. A reschedule is attendance without a decision.
 * CLOSE-RATE DENOMINATOR (Mako, R21/R24, 2026-08-09).
 * SQL twin: is_decisive() — NOT YET DEPLOYED, see [altara]_METRIC_RPC_SSOT.md.
 *
 * ⚠ NAME COLLISION, do not resolve by guessing. Three other things in this codebase
 * already use the word "decisive" and none of them mean this set:
 *   - evaluate_daily_kpi_compliance's CTE `decisive` = shows (5 statuses, includes rescheduled)
 *   - the same function's wire key 'decisive' = the same 5
 *   - hooks/useOperationsAnalytics.ts = PIF+CPP+lost+no_show (renamed to isTerminalOutcome 2026-08-09)
 * Renaming the SQL side is pending Mako. Until then this is the ONLY TS meaning.
 */
export const DECISIVE_STATUSES = [
  "paid_in_full",
  "closed_payment_plan",
  "lost",
  "pending_close",
] as const;

/**
 * Statuses whose cash_collected counts toward cash.
 * Wins + installments. SQL twin: sql/canonical/metric_predicates.sql is_cash_status().
 */
export const CASH_STATUSES = ["paid_in_full", "closed_payment_plan", "paying_installment"] as const;

/** Check if a transaction status counts as a Decisive Call (reached a yes or a no) */
export function isDecisiveCall(status: string | null | undefined): boolean {
  return DECISIVE_STATUSES.includes(status as (typeof DECISIVE_STATUSES)[number]);
}

/** Check if a transaction status counts as a Show (lead was present on call) */
export function isShow(status: string | null | undefined): boolean {
  return SHOW_STATUSES.includes(status as (typeof SHOW_STATUSES)[number]);
}

/** Check if a transaction status contributes cash_collected toward cash metrics */
export function isCashStatus(status: string | null | undefined): boolean {
  return CASH_STATUSES.includes(status as (typeof CASH_STATUSES)[number]);
}
